import { Body, Ecliptic, GeoVector, JupiterMoons, Vector } from "astronomy-engine";
import { makeRand } from "../solar/textures";

/**
 * Major moons for the /sys fly page — two tiers of honesty:
 *
 *  • "ephemeris" — the moon's CURRENT angle around its planet is real,
 *    computed from astronomy-engine: Earth's Moon (GeoVector) and
 *    Jupiter's four Galilean moons (JupiterMoons). Tonight's actual
 *    arrangement of Io/Europa/Ganymede/Callisto — what you'd see
 *    through a telescope.
 *
 *  • "modeled" — astronomy-engine has no ephemeris for these, so the
 *    orbital PERIOD is real but the starting phase is a deterministic
 *    seed advanced by real elapsed time since J2000 (a faithful clock,
 *    not a measured position). Covers Titan, Triton, Phobos, etc.
 *
 * Orbit radii and sizes are display-eased like the planets (true scale
 * would put every moon sub-pixel or off-screen).
 */

export type MoonDef = {
  name: string;
  planet: string;
  /** sidereal orbital period, days (real) */
  periodDays: number;
  /** display orbit radius, multiple of parent display size */
  orbitFactor: number;
  /** display size, multiple of parent display size */
  sizeFactor: number;
  fidelity: "ephemeris" | "modeled";
  /** -1 = retrograde (Triton really does orbit backwards) */
  direction: 1 | -1;
  seed: number;
};

export type MoonState = MoonDef & {
  /** angle around the parent at page-load time, radians */
  angle0: number;
};

export const MOONS: MoonDef[] = [
  { name: "moon",     planet: "earth",   periodDays: 27.32,  orbitFactor: 2.6, sizeFactor: 0.27, fidelity: "ephemeris", direction: 1,  seed: 101 },
  { name: "phobos",   planet: "mars",    periodDays: 0.319,  orbitFactor: 2.2, sizeFactor: 0.13, fidelity: "modeled",   direction: 1,  seed: 201 },
  { name: "deimos",   planet: "mars",    periodDays: 1.263,  orbitFactor: 3.1, sizeFactor: 0.10, fidelity: "modeled",   direction: 1,  seed: 202 },
  { name: "io",       planet: "jupiter", periodDays: 1.769,  orbitFactor: 2.0, sizeFactor: 0.11, fidelity: "ephemeris", direction: 1,  seed: 301 },
  { name: "europa",   planet: "jupiter", periodDays: 3.551,  orbitFactor: 2.5, sizeFactor: 0.10, fidelity: "ephemeris", direction: 1,  seed: 302 },
  { name: "ganymede", planet: "jupiter", periodDays: 7.155,  orbitFactor: 3.1, sizeFactor: 0.15, fidelity: "ephemeris", direction: 1,  seed: 303 },
  { name: "callisto", planet: "jupiter", periodDays: 16.689, orbitFactor: 3.8, sizeFactor: 0.14, fidelity: "ephemeris", direction: 1,  seed: 304 },
  { name: "titan",    planet: "saturn",  periodDays: 15.945, orbitFactor: 3.4, sizeFactor: 0.16, fidelity: "modeled",   direction: 1,  seed: 401 },
  { name: "rhea",     planet: "saturn",  periodDays: 4.518,  orbitFactor: 2.7, sizeFactor: 0.09, fidelity: "modeled",   direction: 1,  seed: 402 },
  { name: "titania",  planet: "uranus",  periodDays: 8.706,  orbitFactor: 2.5, sizeFactor: 0.15, fidelity: "modeled",   direction: 1,  seed: 501 },
  { name: "triton",   planet: "neptune", periodDays: 5.877,  orbitFactor: 2.6, sizeFactor: 0.17, fidelity: "modeled",   direction: -1, seed: 601 },
];

const TWO_PI = Math.PI * 2;
const J2000_MS = Date.UTC(2000, 0, 1, 12);

/** Ecliptic angle of an EQJ vector, radians. */
const eclipticAngle = (v: Vector): number => (Ecliptic(v).elon * Math.PI) / 180;

export const getMoonStates = (date: Date): MoonState[] => {
  // Real jovicentric vectors for the Galilean moons, one call for all four.
  const jm = JupiterMoons(date);
  const jovian: Record<string, number> = {
    io: eclipticAngle(new Vector(jm.io.x, jm.io.y, jm.io.z, jm.io.t)),
    europa: eclipticAngle(new Vector(jm.europa.x, jm.europa.y, jm.europa.z, jm.europa.t)),
    ganymede: eclipticAngle(new Vector(jm.ganymede.x, jm.ganymede.y, jm.ganymede.z, jm.ganymede.t)),
    callisto: eclipticAngle(new Vector(jm.callisto.x, jm.callisto.y, jm.callisto.z, jm.callisto.t)),
  };

  const daysSinceJ2000 = (date.getTime() - J2000_MS) / 86400000;

  return MOONS.map((def) => {
    let angle0: number;
    if (def.name === "moon") {
      // Geocentric ecliptic longitude — the Moon's real angle around Earth.
      angle0 = eclipticAngle(GeoVector(Body.Moon, date, false));
    } else if (def.fidelity === "ephemeris") {
      angle0 = jovian[def.name];
    } else {
      // Real period, seeded phase, advanced by real time since J2000.
      const phase = makeRand(def.seed)() * TWO_PI;
      angle0 = phase + def.direction * TWO_PI * ((daysSinceJ2000 % def.periodDays) / def.periodDays);
    }
    return { ...def, angle0 };
  });
};

/** Moon angle after `simDays` of (possibly accelerated) elapsed time. */
export const moonAngle = (m: MoonState, simDays: number): number =>
  m.angle0 + m.direction * TWO_PI * (simDays / m.periodDays);
